import { deserialize, DocumentObject } from '@jsonapiworld/jsonapi-serializer';
import { backendApiGateway } from '~/client/backend';
import { MetaDataCursorPage } from '~/types/jsonapi';
import {
  BulkPaymentImportConfig,
  Payment,
  PaymentBeneficiaryApprovalPayload,
  PaymentBeneficiaryCheckPayload,
  PaymentBeneficiaryCheckResponse,
  PaymentFetchParameter,
  PaymentsSortFields,
} from '~/types/payment';
import {
  buildJsonAPIQueryParams,
  jsonAPICursorPaginatorBuilder,
  jsonAPIFilterBuilder,
  jsonAPIIncludeBuilder,
  jsonAPISortBuilder,
  SortInfo,
} from '~/utils/jsonapi-query';

export const PaymentPageSize = 50;

export const fetchBulkPaymentImportConfig = async ({ companyId }: { companyId: string }) => {
  const { data } = await backendApiGateway.get<{ config: BulkPaymentImportConfig }>(
    `/api/v1/payments/${companyId}/bulk-payment-import-config`,
  );
  return data.config;
};

export const cancelPayment = async (paymentId: string) => {
  await backendApiGateway.post(`/api/v1/payments/${paymentId}/cancel`);
};

export const approvePaymentBeneficiary = async (beneficiaryApprovalPayload: PaymentBeneficiaryApprovalPayload) => {
  await backendApiGateway.patch(`/api/v1/payments/approve-beneficiary`, beneficiaryApprovalPayload, {
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

export const checkPaymentBeneficiary = async (beneficiaryCheckPayload: PaymentBeneficiaryCheckPayload) => {
  const { data } = await backendApiGateway.post<PaymentBeneficiaryCheckResponse>(
    `/api/v1/payments/check-beneficiary`,
    beneficiaryCheckPayload,
    {
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );

  return data;
};

interface PaymentsMeta {
  page: MetaDataCursorPage;
}

export type FetchPaymentsParams = PaymentFetchParameter & SortInfo<PaymentsSortFields>;

export const fetchPayments = async (filters: FetchPaymentsParams) => {
  const { companyId, search, dateRange, pageParam, status, sort, direction, payeeIdentifier, pageSize } = filters;
  const filterBuilder = jsonAPIFilterBuilder();

  if (companyId) {
    filterBuilder.eq('company.id', companyId);
  }
  if (search) filterBuilder.ilike('search', search);

  if (status) filterBuilder.in('status', status);

  const [startDate, endDate] = dateRange ?? [null, null];
  if (startDate) {
    filterBuilder.gte('requestedExecutionAt', startDate);
  }
  if (endDate) {
    filterBuilder.lt('requestedExecutionAt', endDate);
  }

  if (payeeIdentifier) {
    filterBuilder.eq('payeeIdentifier', payeeIdentifier);
  }

  const sortingBuilder = jsonAPISortBuilder([
    {
      sort,
      direction,
    },
  ]);

  const includeBuilder = jsonAPIIncludeBuilder([
    'account',
    'account.consent',
    'account.consent.institution',
    'counterpart',
    'attachments',
    'transactions',
    'parent',
    'retry_attempts',
    'flows',
  ]);

  const paginationBuilder = jsonAPICursorPaginatorBuilder();
  paginationBuilder.size(pageSize ?? PaymentPageSize);
  if (pageParam) {
    paginationBuilder.cursor(pageParam);
  }
  const queryParams = buildJsonAPIQueryParams(filterBuilder, sortingBuilder, includeBuilder, paginationBuilder);
  const { data } = await backendApiGateway.get<DocumentObject & { meta: PaymentsMeta }>('/api/v1/payments', {
    params: queryParams,
  });

  return {
    payments: deserialize<Payment>(data) as Payment[],
    page: (data.meta as PaymentsMeta).page,
  };
};

export const fetchLatestPaymentByPayeeIdentifier = async ({
  companyId,
  payeeIdentifier,
}: {
  companyId: string | number | null;
  payeeIdentifier: string;
}) => {
  if (!companyId) return null;

  const { payments } = await fetchPayments({
    companyId,
    payeeIdentifier,
    pageSize: 1,
    sort: 'requestedExecutionAt',
    direction: 'desc',
  });

  return payments[0] ?? null;
};
